import { useState } from "react"
import PubSub from "pubsub-js"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Button from "aldoo-ra/Button"
import { CheckCircleIcon, ArrowPathIcon } from "@heroicons/react/24/outline"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import AldooStoreAPI from "aldoo-ra/Store/api"

const { useAuth } = PlatformAuth

export default function PaymentSuccess({ store, children }) {
  const texts = TextBundle({ id: "aldoo-store" })
  const [auth, setAuth] = useAuth()
  const [showValidationModal, setShowValidationModal] = useState(false)
  const [validationStatus, setValidationStatus] = useState('checking')
  const [validationError, setValidationError] = useState(null)

  const { paymentReceipt, setPaymentSuccessVisible } = store
  const {
    paymentInfo,
    paymentIntentId,
    subscriptionId,
    requiresVerification,
    existingUserEmail,
    existingUserCredentials
  } = paymentReceipt || {}

  const handleGoToAccount = async () => {
    if (requiresVerification && existingUserCredentials) {
      setValidationStatus('awaiting-verification')
      setShowValidationModal(true)
      await initiateLoginVerification()
      return
    }

    const isFreeType = paymentInfo?.type === "free_purchase" || paymentInfo?.type === "free_subscription"

    if (isFreeType) {
      window.location.href = "/account"
      return
    }

    if (!paymentIntentId && !subscriptionId) {
      window.location.href = "/account"
      return
    }

    setShowValidationModal(true)
    setValidationStatus('checking')
    setValidationError(null)

    setTimeout(() => {
      pollForValidation()
    }, 100)
  }

  const initiateLoginVerification = () => {
    setShowValidationModal(false)

    if (auth) {
      window.location.href = "/account"
      return
    }

    setPaymentSuccessVisible(false)

    PubSub.publish("signin", {
      redirect: "/account",
      email: existingUserEmail,
      provider: "email"
    })
  }

  const pollForValidation = async () => {
    let attempts = 0
    const maxAttempts = 60

    const checkValidation = async () => {
      try {
        attempts++
        const result = await AldooStoreAPI.checkPaymentProcessing(paymentIntentId, subscriptionId)

        if (result.processed) {
          setValidationStatus('validated')
          await PlatformAuth.refreshProfile()
          setTimeout(() => {
            window.location.href = "/account"
          }, 500)
          return true
        } else {
          if (attempts >= maxAttempts) {
            setValidationStatus('error')
            setValidationError('Purchase validation timed out. Your purchase was successful, but processing is taking longer than expected. Please check your account in a few moments.')
            return true
          }
          return false
        }
      } catch (error) {
        if (attempts >= maxAttempts) {
          setValidationStatus('error')
          setValidationError('Unable to validate purchase. Your payment was successful. Please check your account in a few moments.')
          return true
        }
        return false
      }
    }

    const shouldStop = await checkValidation()
    if (shouldStop) return

    const pollInterval = setInterval(async () => {
      const shouldStop = await checkValidation()
      if (shouldStop) {
        clearInterval(pollInterval)
      }
    }, 1000)
  }

  const handleCloseModal = () => {
    setShowValidationModal(false)
  }

  const handleRetryValidation = () => {
    setValidationStatus('checking')
    setValidationError(null)
    pollForValidation()
  }

  const getSuccessMessage = () => {
    if (paymentInfo.type === "free_subscription") {
      return texts?.free_subscription_success || "Your free subscription has been activated successfully!"
    }
    if (paymentInfo.type === "free_purchase") {
      return texts?.free_purchase_success || "Your free purchase has been activated successfully!"
    }
    if (paymentInfo.hasTrial) {
      return texts?.trial_success || "Your trial has been activated."
    }
    if (paymentInfo.type === "subscription") {
      return texts?.subscription_success || "Your subscription has been activated successfully!"
    }
    if (paymentInfo.type === "purchase") {
      return texts?.payment_success || "Your payment was processed successfully!"
    }
  }

  return (
    <>
      {!showValidationModal && (
        <div className="p-6 flex flex-col items-center text-center">
          <CheckCircleIcon className="w-16 h-16 text-primary mb-4" />

          <Typography component="h1" variant="h2" className="text-text mb-4">
            {texts?.thank_you || "Thank You!"}
          </Typography>

          <Typography variant="txt1_dim" className="mb-6">
            {getSuccessMessage()}
          </Typography>

          {(auth || requiresVerification) && (
            <div className="w-full flex justify-center mb-6">
              <Button variant="primary" onClick={handleGoToAccount}>
                {texts?.go_to_account || "Go to My Account"}
              </Button>
            </div>
          )}

          {children && (
            <div className="w-full border-t border-bordered pt-6 mt-2">
              {children}
            </div>
          )}
        </div>
      )}

      {showValidationModal && (
        <div className="p-6 flex flex-col items-center text-center min-w-[300px]">
          {validationStatus === 'checking' && (
            <>
              <ArrowPathIcon className="w-12 h-12 text-primary mb-4 animate-spin" />
              <Typography variant="h3" className="mb-2">{texts?.accessing_account || "Accessing Account..."}</Typography>
              <Typography variant="txt1_dim">{texts?.please_wait_verify || "Please wait while we verify your account"}</Typography>
            </>
          )}

          {validationStatus === 'awaiting-verification' && (
            <>
              <CheckCircleIcon className="w-12 h-12 text-primary mb-4" />
              <Typography variant="h3" className="mb-2">{texts?.verify_email_title || "Please Verify Your Email"}</Typography>
              <Typography variant="txt1" className="block text-center mb-2">
                {texts?.verification_link_sent || "We've sent a verification link to"} <strong>{existingUserEmail}</strong>
              </Typography>
              <Typography variant="txt1_dim" className="text-center">
                {texts?.click_email_link || "Click the link in your email to sign in and access your account"}
              </Typography>
            </>
          )}

          {validationStatus === 'validated' && (
            <>
              <CheckCircleIcon className="w-12 h-12 text-success mb-4" />
              <Typography variant="h3" className="mb-2">{texts?.purchase_validated || "Purchase Validated"}</Typography>
              <Typography variant="txt1_dim">{texts?.redirecting || "Redirecting to your account..."}</Typography>
            </>
          )}

          {validationStatus === 'error' && (
            <>
              <Typography variant="h3" className="mb-4">{texts?.validation_delayed || "Validation Delayed"}</Typography>
              <Typography variant="txt1_dim" className="block mb-4">{validationError}</Typography>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={handleCloseModal}>{texts?.close || "Close"}</Button>
                <Button variant="primary" onClick={handleRetryValidation}>{texts?.retry || "Retry"}</Button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}
